import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Baby, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TilloPanel() {
  const [open, setOpen] = useState(false);
  const [eps, setEps] = useState([]);
  const emptyForm = { title: "", description: "", song_title: "", video_slug: "", order: 99, lyrics: "" };
  const [form, setForm] = useState(emptyForm);

  const load = () => axios.get(`${API}/tillo/episodes`).then((r) => setEps(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    try {
      await axios.post(`${API}/tillo/episodes`, form);
      toast.success("Tillo episode saved");
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const edit = (ep) => {
    setForm({ id: ep.id, title: ep.title || "", description: ep.description || "", song_title: ep.song_title || "", video_slug: ep.video_slug || "", order: ep.order ?? 99, lyrics: ep.lyrics || "" });
  };

  const attach = async (ep) => {
    const slug = window.prompt("Storylo video slug or ID to attach to this episode:", ep.video_slug || "");
    if (slug === null) return;
    try {
      await axios.post(`${API}/tillo/episodes`, { ...ep, video_slug: slug });
      toast.success("Video attached");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Attach failed"); }
  };

  const remove = async (ep) => {
    if (!window.confirm(`Delete Tillo episode "${ep.title}"?`)) return;
    await axios.delete(`${API}/tillo/episodes/${ep.id}`).catch(() => {});
    load();
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1.5" data-testid="tillo-panel-open">
        <Baby className="w-3 h-3" /> Tillo
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#14120d] p-5 my-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tillo-panel-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg">Tillo episodes</h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            {eps.map((ep) => (
              <div key={ep.id} className="rounded-lg border border-white/10 p-3 mb-2 text-sm">
                <div className="font-semibold">{ep.title} {ep.video_url ? "🎬" : "· coming soon"}</div>
                <div className="text-xs text-[color:var(--muted)]">Song: {ep.song_title || "—"} · Video: {ep.video_slug || "none"} · Lyrics: {ep.lyrics ? "✓" : "—"}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => edit(ep)} className="text-xs px-2 py-1 rounded border border-white/15 hover:border-[color:var(--accent)]" data-testid={`tillo-edit-${ep.id}`}>Edit / lyrics</button>
                  <button onClick={() => attach(ep)} className="text-xs px-2 py-1 rounded border border-white/15 hover:border-[color:var(--accent)]" data-testid={`tillo-attach-${ep.id}`}>Attach video</button>
                  <button onClick={() => remove(ep)} className="text-xs px-2 py-1 rounded border border-white/15 hover:border-red-400 hover:text-red-300">Delete</button>
                </div>
              </div>
            ))}
            <div className="mt-4 border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-widest text-[color:var(--muted)] mb-2">{form.id ? "Edit episode" : "Add episode"}</div>
              {["title", "song_title", "video_slug"].map((k) => (
                <input key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={k.replace("_", " ")} className="w-full mb-2 bg-black/30 border border-white/10 rounded px-3 py-2 text-sm" data-testid={`tillo-form-${k}`} />
              ))}
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" className="w-full mb-2 bg-black/30 border border-white/10 rounded px-3 py-2 text-sm" rows={2} data-testid="tillo-form-description" />
              <textarea value={form.lyrics} onChange={(e) => setForm({ ...form, lyrics: e.target.value })} placeholder="Song lyrics (blank line between verses) — shown on the Sing-Along page" className="w-full mb-2 bg-black/30 border border-white/10 rounded px-3 py-2 text-sm" rows={6} data-testid="tillo-form-lyrics" />
              <button onClick={save} className="btn-primary text-sm px-4 py-2 w-full" data-testid="tillo-form-save">{form.id ? "Update episode" : "Save episode"}</button>
              {form.id && (
                <button onClick={() => setForm(emptyForm)} className="mt-2 text-xs px-3 py-1.5 rounded border border-white/15 w-full" data-testid="tillo-form-cancel-edit">Cancel edit</button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
